import React, { useContext, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import { useNewsfeed } from "../context/NewsfeedContext";
import api from "../services/api";
import { useNotificationSocket, SocketContext } from "../context/SocketContext";
import {
  FaHome,
  FaSearch,
  FaBell,
  FaUser,
  FaUsers,
  FaSignOutAlt,
  FaUserPlus,
  FaSignInAlt,
  FaArrowLeft
} from "react-icons/fa";

const Navbar = ({ onToggleNotifications, notificationPanel }) => {
  const { user, logout, endSession } = useContext(AuthContext);
  const { disconnectSocket } = useContext(SocketContext);
  const { surveyToken, condition, conditionConfig } = useContext(SurveyContext);
  const { newContentAvailable, triggerRefresh } = useNewsfeed();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSurveyButtonActive, setIsSurveyButtonActive] = useState(false);
  const [showTimerNotification, setShowTimerNotification] = useState(false);
  const [platformEntered, setPlatformEntered] = useState(false);
  const [minutesRemaining, setMinutesRemaining] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Detect when participant enters the platform (newsfeed, search, profile, etc.)
  // Only starts when user is authenticated — prevents timer from running during
  // registration/onboarding which happens before the actual feed is visible.
  // Persist entry time in sessionStorage so it survives refresh.
  useEffect(() => {
    if (!user) return; // Wait until authenticated
    const platformPaths = ["/newsfeed", "/search", "/profile", "/messages", "/notifications"];
    if (platformPaths.some(p => location.pathname.startsWith(p))) {
      setPlatformEntered(true);
      if (!sessionStorage.getItem("spiders_platform_entered_at")) {
        sessionStorage.setItem("spiders_platform_entered_at", String(Date.now()));
      }
    }
  }, [location.pathname, user]);

  useEffect(() => {
    // In test mode, activate button immediately
    if (window.__studyTestMode) {
      setIsSurveyButtonActive(true);
      return;
    }
    // Only start the timer once the participant has entered the platform
    if (!platformEntered) return;

    // Read timer from study config (via window) or condition config, fallback to 10 min
    const studyMinDuration = window.__studySessionConfig?.minDurationMinutes;
    const conditionDuration = conditionConfig?.session?.durationMinutes;
    const minutes = studyMinDuration || conditionDuration || 10;

    // Use persisted entry time so the timer survives page refresh
    const enteredAt = parseInt(sessionStorage.getItem("spiders_platform_entered_at") || String(Date.now()));
    const endTime = enteredAt + minutes * 60 * 1000;
    const remaining = endTime - Date.now();

    // Already expired (e.g., refreshed after 10 min)
    if (remaining <= 0) {
      setIsSurveyButtonActive(true);
      setMinutesRemaining(0);
      if (window.__studyMode) setShowTimerNotification(true);
      return;
    }

    // Update remaining minutes every 30s
    const updateRemaining = () => {
      const left = Math.ceil((endTime - Date.now()) / 60000);
      setMinutesRemaining(left > 0 ? left : 0);
    };
    updateRemaining();
    const interval = setInterval(updateRemaining, 30000);

    const timer = setTimeout(() => {
      setIsSurveyButtonActive(true);
      setMinutesRemaining(0);
      if (window.__studyMode) {
        setShowTimerNotification(true);
      }
    }, remaining);

    return () => { clearTimeout(timer); clearInterval(interval); };
  }, [conditionConfig, platformEntered]);

  // Agent silence detection: if no new content for 3 minutes, enable early exit
  // Only runs after participant has entered the platform
  useEffect(() => {
    if (!window.__studyMode || isSurveyButtonActive) return;
    if (!platformEntered) return;
    const SILENCE_THRESHOLD_MS = 3 * 60 * 1000; // 3 minutes

    const check = setInterval(() => {
      const lastActivity = window.__lastAgentActivityTime || Date.now();
      if (Date.now() - lastActivity > SILENCE_THRESHOLD_MS) {
        setIsSurveyButtonActive(true);
        setShowTimerNotification(true);
        clearInterval(check);
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(check);
  }, [isSurveyButtonActive]);

  useEffect(() => {
    let poll;
    const fetchUnread = async () => {
      try {
        if (!user || !condition) return;
        const { data } = await api.get("/notifications/unread-count", {
          params: { condition },
        });
        setUnreadCount(data.count || 0);
      } catch (e) {
        console.error("Failed to load unread notifications:", e);
      }
    };
    fetchUnread();
    poll = setInterval(fetchUnread, 30000);
    return () => clearInterval(poll);
  }, [user, condition]);

  // Listen for "mark all read" from NotificationPanel
  useEffect(() => {
    const handler = () => setUnreadCount(0);
    window.addEventListener("spiders:notifications-read", handler);
    return () => window.removeEventListener("spiders:notifications-read", handler);
  }, []);

  // Real-time badge increment from socket
  useNotificationSocket(
    React.useCallback((notification) => {
      if (notification.actorId !== user?.userId) {
        setUnreadCount((prev) => prev + 1);
      }
    }, [user?.userId])
  );

  // Click outside to close dropdown
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleHomeClick = () => {
    triggerRefresh();
    navigate('/newsfeed');
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleReturnToSurvey = async () => {
    // Study mode: trigger post-survey via StudyFlow callback
    if (window.__studyMode && window.__studyPlatformDone) {
      if (
        window.confirm(
          "Are you sure you want to continue to the post-study questionnaire?"
        )
      ) {
        try {
          await endSession();
        } catch (error) {
          console.error("Failed to end session:", error);
        }
        disconnectSocket();
        window.__studyPlatformDone();
      }
      return;
    }

    // Non-study mode: build redirect URL from condition config or use default
    const buildRedirectUrl = () => {
      const redirectConfig = conditionConfig?.completionRedirect;

      if (!redirectConfig?.url) {
        if (surveyToken) {
          return `https://www.soscisurvey.de/spiders/?i=${surveyToken}`;
        }
        return null;
      }

      let url = redirectConfig.url;
      if (redirectConfig.includeParticipantId !== false && surveyToken) {
        const paramName = redirectConfig.participantIdParam || "i";
        const separator = url.includes("?") ? "&" : "?";
        url = `${url}${separator}${paramName}=${surveyToken}`;
      }

      return url;
    };

    const redirectUrl = buildRedirectUrl();

    if (redirectUrl) {
      if (
        window.confirm(
          "Are you sure you want to return to the survey? You will leave this page."
        )
      ) {
        try {
          await endSession();
          disconnectSocket();
          window.location.href = redirectUrl;
        } catch (error) {
          console.error("Failed to end session:", error);
        }
      }
    } else {
      alert(
        "You were not redirected from a survey. This button is only for survey participants."
      );
    }
  };

  return (
    <nav className="text-spiders-text-primary h-screen p-4 w-64 bg-spiders-bg-dark border-r border-spiders-border flex flex-col justify-between z-10 sticky top-0">
      {/* SPIDERS Logo */}
      <div className="mb-8 pb-4 border-b border-spiders-border">
        <button onClick={handleHomeClick} className="flex items-center space-x-3 hover:bg-spiders-bg-elevated/50 p-2 rounded-lg transition-colors duration-200 w-full text-left group">
          <div className="relative w-10 h-10">
            <svg viewBox="0 0 40 40" className="w-full h-full text-spiders-primary-400 group-hover:text-spiders-accent-400 transition-colors duration-300">
              {/* Spider web */}
              <g stroke="currentColor" fill="none" strokeWidth="1.5">
                {/* Radial lines */}
                <line x1="20" y1="5" x2="20" y2="35" opacity="0.6" />
                <line x1="5" y1="20" x2="35" y2="20" opacity="0.6" />
                <line x1="8.5" y1="8.5" x2="31.5" y2="31.5" opacity="0.6" />
                <line x1="31.5" y1="8.5" x2="8.5" y2="31.5" opacity="0.6" />

                {/* Concentric hexagons */}
                <polygon points="20,8 28,14 28,26 20,32 12,26 12,14" opacity="0.8" />
                <polygon points="20,12 24,16 24,24 20,28 16,24 16,16" opacity="0.9" />
                <polygon points="20,16 22,18 22,22 20,24 18,22 18,18" fill="currentColor" />
              </g>

              {/* Small spider */}
              <g fill="currentColor">
                <circle cx="26" cy="15" r="1.5" opacity="0.8" />
                <line x1="24" y1="14" x2="28" y2="16" stroke="currentColor" strokeWidth="0.8" opacity="0.6" />
                <line x1="24" y1="16" x2="28" y2="14" stroke="currentColor" strokeWidth="0.8" opacity="0.6" />
              </g>
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gradient-logo">SPIDERS</h1>
            <p className="text-xs text-spiders-text-muted group-hover:text-spiders-text-secondary transition-colors duration-300">Research Platform</p>
          </div>
        </button>
      </div>
      
      <ul className="space-y-2">
        {user ? (
          <>
            <li>
              <button
                className="spiders-nav-link w-full relative"
                onClick={handleHomeClick}
              >
                <FaHome className="w-5 h-5 mr-4" />
                Home
                {newContentAvailable && (
                  <span className="absolute top-2 right-2 bg-spiders-accent-400 rounded-full w-2 h-2 animate-pulse"></span>
                )}
              </button>
            </li>
            <li>
              <Link
                className="spiders-nav-link"
                to="/search"
              >
                <FaSearch className="w-5 h-5 mr-4" />
                Search
              </Link>
            </li>
            <li>
              <button
                className="spiders-nav-link w-full"
                onClick={onToggleNotifications}
              >
                <FaBell className="w-5 h-5 mr-4" />
                Notifications {unreadCount > 0 && (
                  <span className="ml-2 spiders-badge">
                    {unreadCount}
                  </span>
                )}
              </button>
            </li>
            <li>
              <Link
                className="spiders-nav-link"
                to={`/profile/${user.username}`}
              >
                <FaUser className="w-5 h-5 mr-4" />
                Profile
              </Link>
            </li>
            {!window.__studyMode && (
              <li>
                <button
                  className="spiders-nav-link w-full"
                  onClick={handleLogout}
                >
                  <FaSignOutAlt className="w-5 h-5 mr-4" />
                  Logout
                </button>
              </li>
            )}
          </>
        ) : (
          <>
            <li>
              <Link
                className="spiders-nav-link"
                to="/register"
              >
                <FaUserPlus className="w-5 h-5 mr-4" />
                Register
              </Link>
            </li>
            <li>
              <Link
                className="spiders-nav-link"
                to="/login"
              >
                <FaSignInAlt className="w-5 h-5 mr-4" />
                Login
              </Link>
            </li>
          </>
        )}
      </ul>
      {user && (
        <div className="mt-auto pt-4 border-t border-spiders-border space-y-2">
          {showTimerNotification && (
            <div className="bg-spiders-accent-600/20 border border-spiders-accent-500/40 rounded-lg p-3 animate-fadeIn">
              <p className="text-xs text-spiders-accent-400 leading-relaxed">
                You can now proceed to the final questionnaire whenever you're ready.
                Feel free to continue exploring if you'd like.
              </p>
              <button
                onClick={() => setShowTimerNotification(false)}
                className="text-[10px] text-spiders-text-muted hover:text-spiders-text-secondary mt-1"
              >
                Dismiss
              </button>
            </div>
          )}
          <button
            className={`flex items-center py-3 px-4 rounded-lg w-full text-left transition-all duration-200 ${
              isSurveyButtonActive
                ? "bg-spiders-accent-600 hover:bg-spiders-accent-700 text-white shadow-accent-glow"
                : "bg-spiders-bg-elevated text-spiders-text-muted cursor-not-allowed"
            }`}
            onClick={handleReturnToSurvey}
            disabled={!isSurveyButtonActive}
          >
            <FaArrowLeft className="w-5 h-5 mr-4" />
            Return to Survey
          </button>
          {!isSurveyButtonActive && minutesRemaining !== null && minutesRemaining > 0 && (
            <p className="text-xs text-spiders-text-muted mt-2 mb-2 text-center">
              Available in ~{minutesRemaining} min
            </p>
          )}
        </div>
      )}
      {/* Legal notice link */}
      <div className="px-4 py-3 border-t border-spiders-border/30">
        <Link
          to="/legal"
          className="text-xs text-spiders-text-muted hover:text-spiders-text-secondary transition-colors"
        >
          Impressum / Legal Notice
        </Link>
      </div>
      {/* Notification panel — rendered inside sticky nav so it stays in position */}
      {notificationPanel}
    </nav>
  );
};

export default Navbar;
